import { fork, call, all, put, takeEvery, takeLatest, select } from 'redux-saga/effects'; // eslint-disable-line
import { RESOURCES } from 'STORE/constants';
import {
  fetchResource,
  saveResource,
  getApiResource,
  getTaskResourceById,
  getStatusById,
  getStatuses,
  getResourceCSV,
} from 'HELPERS/resources';
import { objectToQuerystring, removeEmptyKeys, getStoreState } from 'HELPERS/utils';
import { history } from 'STORE/configureStore';
import queryString from 'query-string';
import { message } from 'antd';
import { saveAs } from 'file-saver';
import moment from 'moment';

const delay = ms => new Promise(res => setTimeout(res, ms));

const errorPage = '/sorry';
const errorApiMessage = 'Something went wrong when contacting Newsroom API, please let Engineering know';

export function* watchFetchResource(action) {
  try {
    let params = removeEmptyKeys(action.params);
    const state = yield select(getStoreState);
    const { router } = state;
    const searchParams = router.location.search;

    // Check if url has some params to be passed to request
    if (searchParams.length && action.type === RESOURCES.FETCH.REQUEST) {
      const searchParsed = queryString.parse(searchParams);
      params = { ...searchParsed, ...params };
    }

    const { resource } = action;
    const apiResource = getApiResource(resource);
    const url = (Object.keys(params).length)
      ? `${apiResource}${objectToQuerystring(params)}`
      : apiResource;
    const response = yield call(fetchResource, url);

    yield put({ type: RESOURCES.FETCH.COMPLETE });

    if (!response || response.status !== 200) {
      message.error(errorApiMessage);
      return yield put({ type: RESOURCES.FETCH.FAILURE });
    }

    if ((action.type === RESOURCES.FETCH.REQUEST || action.type === RESOURCES.REFETCH.REQUEST)
      && Object.keys(params).length) {
      yield fork(setResourceUrlParams, params);
    }

    const json = yield call([response, 'json']);

    yield put({
      type: RESOURCES.FETCH.SUCCESS,
      resource: action.resource,
      payload: { ...json, params },
    });
  } catch (error) {
    yield put({ type: RESOURCES.FETCH.FAILURE, resource: action.resource });
    return yield history.push({ pathname: errorPage });
  }
}

export function* watchFetchResourceCSV({ resource, resourceTypeId, params }) {
  try {
    const response = yield call(getResourceCSV, resource, resourceTypeId, params);
    if (!response || response.status !== 200) {
      message.error(errorApiMessage);
      return yield put({ type: RESOURCES.TASKS_CSV.FAILURE });
    }

    // Save the blorb!
    const blob = yield call([response, 'blob']);
    const filename = `avenue-${resource}-${moment(new Date()).format('MM-DD-YYYY')}.csv`;
    yield saveAs(blob, filename);
  } catch (error) {
    return yield history.push({ pathname: errorPage });
  }
}

export function* watchRemoveResource({ resource, ids }) {
  try {
    const apiResource = getApiResource(resource);
    const response = yield call(saveResource, `${apiResource}/delete`, { task_ids: ids });
    if (response.status !== 204) {
      message.error(errorApiMessage);
      return yield put({ type: RESOURCES.REMOVE.FAILURE });
    }

    yield put({ type: RESOURCES.TASKS_SUMMARY.REQUEST });
    yield put({ type: RESOURCES.REMOVE.SUCCESS, resource, ids });
    yield put({ type: RESOURCES.FETCH.REQUEST, resource, params: {} });
  } catch (error) {
    yield put({ type: RESOURCES.REMOVE.FAILURE, resource });
    return yield history.push({ pathname: errorPage });
  }
}

export function* watchRemoveAllResource({ resource, typeId }) {
  try {
    const apiResource = getApiResource(resource);
    const response = yield call(saveResource, `${apiResource}/delete`, { type_id: typeId });

    if (response.status !== 204) {
      message.error(errorApiMessage);
      return yield put({ type: RESOURCES.REMOVE_ALL.FAILURE });
    }

    yield put({ type: RESOURCES.REMOVE_ALL.SUCCESS, resource });
    yield put({ type: RESOURCES.TASKS_SUMMARY.RESET, resource });
  } catch (error) {
    yield put({ type: RESOURCES.REMOVE_ALL.FAILURE, resource });
    return yield history.push({ pathname: errorPage });
  }
}

export function* watchSaveResource({ resource, data, options }) {
  try {
    const response = yield call(saveResource, resource, data, options);

    if (!response) {
      // eslint-disable-next-line no-console
      console.error('No response returned from the API');
      return yield put({ type: RESOURCES.SAVE.FAILURE, error: 'No response returned from the API' });
    }

    if (response.status !== 200) {
      message.error(errorApiMessage);
      return yield put({ type: RESOURCES.SAVE.FAILURE, error: errorApiMessage });
    }

    let json;
    try {
      json = yield call([response, 'json']);
    } catch (parseError) {
      // eslint-disable-next-line no-console
      console.error('Error parsing JSON:', parseError);
      return yield put({ type: RESOURCES.SAVE.FAILURE, resource, error: 'Error parsing JSON' });
    }

    yield put({ type: RESOURCES.SAVE.SUCCESS, resource, responseData: json });

    if (resource === 'settings') {
      yield put({ type: RESOURCES.FETCH.REQUEST, resource, params: {} });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Save Resource Error:', error); // Log the error for debugging
    yield put({ type: RESOURCES.SAVE.FAILURE, resource, error });
    yield history.push({ pathname: errorPage });
  }
}

export function* setResourceUrlParams(updateParams) {
  const state = yield select(getStoreState);
  const { location } = state.router;
  const searchParsed = queryString.parse(location.search);
  const newParams = { ...searchParsed, ...updateParams };
  const newSearch = queryString.stringify(newParams);

  return yield history.push({ pathname: location.pathname, search: newSearch });
}

export function* clearFilters(action) {
  const state = yield select(getStoreState);
  const { location } = state.router;
  const searchParsed = queryString.parse(location.search);
  action.filters.forEach((filter) => {
    delete searchParsed[filter];
  });
  const newSearch = queryString.stringify(searchParsed);
  yield history.push({ pathname: location.pathname, search: newSearch });
  return yield put({
    type: RESOURCES.REFETCH.REQUEST,
    resource: action.resource,
    params: searchParsed,
  });
}

export function* watchSummaryRequests() {
  return yield fork(getTasksSummary);
}

export function* getTasksSummary() {
  const state = yield select(getStoreState);
  const { resources } = state;

  const response = yield call(fetchResource, 'tasks/summary');
  if (!response || response.status !== 200) {
    message.error(errorApiMessage);
    return yield put({ type: RESOURCES.TASKS_SUMMARY.FAILURE });
  }

  const json = yield call([response, 'json']);
  const statuses = getStatuses();
  const resetStatuses = statuses.reduce((obj, item) => {
    // eslint-disable-next-line
    obj[item.name] = 0;
    return obj;
  }, {});

  const resourcesDataSummary = {};
  const types = json.reduce((h, a) => Object.assign(h, {
    [a.type_id]: (h[a.type_id] || [])
      .concat(a),
  }), {});
  Object.keys(types).forEach((typeId) => {
    const group = types[typeId];
    const resourceName = getTaskResourceById(group[0].type_id);
    if (!resources[resourceName]) {
      return;
    }
    const updateStatuses = {};
    const updates = {};
    group.forEach((result) => {
      const status = getStatusById(result.status_id);
      updateStatuses[status.name] = result.count;
      updates[status.name] = result.last_update;
      resourcesDataSummary[resourceName] = {
        ...resources[resourceName].summary,
        lastUpdate: updates,
        statuses: {
          ...resetStatuses,
          ...updateStatuses,
        },
      };
    });
  });

  yield all(Object.keys(resourcesDataSummary).map(resourceKey => put({
    type: RESOURCES.TASKS_SUMMARY.SUCCESS,
    resource: resourceKey,
    payload: resourcesDataSummary[resourceKey],
  })
  ));

  yield delay(5000);
  yield put({ type: RESOURCES.TASKS_SUMMARY.REQUEST });
}

export default function* rootSagas() {
  yield takeEvery(RESOURCES.FETCH.REQUEST, watchFetchResource);
  yield takeEvery(RESOURCES.FETCH_SIMPLE.REQUEST, watchFetchResource);
  yield takeEvery(RESOURCES.REFETCH.REQUEST, watchFetchResource);
  yield takeEvery(RESOURCES.REMOVE.REQUEST, watchRemoveResource);
  yield takeEvery(RESOURCES.REMOVE_ALL.REQUEST, watchRemoveAllResource);
  yield takeEvery(RESOURCES.SAVE.REQUEST, watchSaveResource);
  yield takeEvery(RESOURCES.FILTERS.CLEAR, clearFilters);
  yield takeLatest(RESOURCES.TASKS_SUMMARY.REQUEST, watchSummaryRequests);
  yield takeLatest(RESOURCES.TASKS_CSV.REQUEST, watchFetchResourceCSV);
}



// WEBPACK FOOTER //
// ./src/store/sagas/resources.sagas.js